import { Component, EventEmitter, Output, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { TreeService } from 'src/app/services/Tree.service';
import { DataService } from 'src/app/services/Data.service';
import { LoadSimulationService, LoadSimulationFilter } from './load-simulation.service';
import { HeaderActionsService, HeaderActionType } from 'src/app/services/header-actions.service';

// --- Interfaces ---
export interface LoadSimulationForm {
    userId: number | null;
    customer: {
        id: number | null;
        description: string;
    };
    region: {
        id: number | null;
        description: string;
    };
    project: {
        id: number | null;
        description: string;
    };
    simulation: {
        id: number | null;
        description: string;
    };
    path: string;
}

@Component({
    selector: 'app-load-simulation-modal',
    templateUrl: './load-simulation-modal.component.html',
    styleUrls: ['./load-simulation-modal.component.css']
})
export class LoadSimulationModalComponent implements OnInit {
    @Output() close = new EventEmitter<void>();

    nodes: any[] = [];
    users: any[] = [];
    customers: any[] = [];
    regions: any[] = [];
    projects: any[] = [];
    simulations: any[] = [];

    userId: number;
    userName: string;
    module: string;
    department: string;
    rol: string;

    filteredSimulations$: Observable<any[]>;
    selectedSimulation$: Observable<any>;

    constructor(
        private treeService: TreeService,
        private dataService: DataService,
        private loadSimulationService: LoadSimulationService,
        private headerActionsService: HeaderActionsService
    ) {
        this.filteredSimulations$ = this.loadSimulationService.filteredSimulations$;
        this.selectedSimulation$ = this.loadSimulationService.selectedSimulation$;
    }

    ngOnInit(): void {
        this.userId = this.dataService.getCurrentUserId();
        this.userName = this.dataService.getCurrentNameSurname();
        this.module = this.dataService.getCurrentModule();
        this.department = this.dataService.getCurrentDepartment();
        this.setRol();
        
        this.loadSimulationsData();
    }

    private setRol() {
        const roles = this.dataService.getCurrentRol();
        if (roles && roles.some(x => x == "ADMIN")) {
            this.rol = 'ADMIN';
        } else if (roles && roles.some(x => x == "SUPER_USER")) {
            this.rol = 'SUPER_USER';
        } else if (roles && roles.some(x => x == "USER")) {
            this.rol = 'USER';
        }
    }

    private loadSimulationsData() {
        if (this.userId && this.module && this.department && this.rol) {
            this.treeService.getNodeTreeByUserIdVisibility(this.userId, this.department, this.rol, this.module, false).subscribe(levels => {
                if (levels.status.success) {
                    this.nodes = levels.data;
                    this.mapNodesToFiltersAndSimulations();
                }
            });
        }
    }

    mapNodesToFiltersAndSimulations() {
        // Map nodes to users, customers, regions, projects, simulations
        this.users = this.nodes.filter(x => x.item_level === 'user').map(x => ({
            id: x.id,
            name: x.item_name,
            selected: x.user_id == this.userId,
            user_id: x.user_id
        }));
        this.customers = this.nodes.filter(x => x.item_level === 'customer').map(x => ({ id: x.id, name: x.item_name, parent_id: x.parent_id }));
        this.regions = this.nodes.filter(x => x.item_level === 'region').map(x => ({ id: x.id, name: x.item_name, parent_id: x.parent_id }));
        this.projects = this.nodes.filter(x => x.item_level === 'project').map(x => ({ id: x.id, name: x.item_name, parent_id: x.parent_id }));
        const simulationsData = this.nodes.filter(x => x.item_level === 'simulation').map(x => ({
            user: this.getParentName(x, 'user'),
            customer: this.getParentName(x, 'customer'),
            region: this.getParentName(x, 'region'),
            project: this.getParentName(x, 'project'),
            parent_id: x.parent_id,
            name: x.item_name,
            id: x.id,
            user_id: x.user_id
        }));
        this.simulations = simulationsData;
        this.loadSimulationService.setSimulations(simulationsData);
    }

    getParentName(node: any, level: string): { id: any; name: string } {
        let current = node;
        const trace = [];
        while (current && current.parent_id) {
            const parent = this.nodes.find(x => x.id === current.parent_id);
            trace.push({
                currentId: current.id,
                currentName: current.item_name,
                currentLevel: current.item_level,
                parentId: parent?.id,
                parentName: parent?.item_name,
                parentLevel: parent?.item_level
            });
            if (!parent) break;
            if (parent.item_level === level) {
                return { id: parent.id, name: parent.item_name };
            }
            current = parent;
        }

        return { id: null, name: '' };
    }

    onFilterChange(filter: LoadSimulationFilter) {
        this.loadSimulationService.setFilters(filter);
    }

    onClose(): void {
        this.loadSimulationService.setSelectedSimulation(null);
        this.close.emit();
    }

    loadSimulation(simulation?: any): void {
        const sim = simulation || this.loadSimulationService.getSelectedSimulationValue();
        if (!sim) {
            return;
        }
        const mapped = this.mapSimulationToLoadSimulationForm(sim);

        this.headerActionsService.requestAction(HeaderActionType.Load, mapped);

        this.onClose();
    }

    private mapSimulationToLoadSimulationForm(sim: any): LoadSimulationForm {
        return {
            userId: sim.user_id ?? null,
            customer: {
                id: sim.customer?.id ?? null,
                description: sim.customer?.name ?? ''
            },
            region: {
                id: sim.region?.id ?? null,
                description: sim.region?.name ?? ''
            },
            project: {
                id: sim.project?.id ?? null,
                description: sim.project?.name ?? ''
            },
            simulation: {
                id: sim.id ?? null,
                description: sim.name ?? ''
            },
            path: [
                sim.customer?.name ?? '',
                sim.region?.name ?? '',
                sim.project?.name ?? '',
                sim.name ?? ''
            ].filter(Boolean).join('-')
        };
    }
}
